/**
 * Tab *Theo tên miền* — FR-075…079, TC-16, TC-26.
 * Bám nguyên `permissions.html` → `[data-panel="by-domain"]`.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { Icon } from '../../components/Icon';
import { AsyncSection, EmptyState, SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';
import { DomainAccessTable } from './DomainAccessPanel';
import { ShareGrantDialog } from './ShareGrantDialog';
import { permApi, permKeys, type PendingGrantView } from './api';

export function ByDomainTab() {
  const t = useT();
  const { can } = useAuth();
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showShare, setShowShare] = useState(false);

  const domainsQuery = useQuery({
    queryKey: permKeys.domains(''),
    queryFn: () => permApi.domains(''),
  });

  const domains = domainsQuery.data ?? [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return domains;
    return domains.filter((domain) => domain.name.toLowerCase().includes(q));
  }, [domains, filter]);

  const selected = domains.find((d) => d.id === selectedId) ?? filtered[0] ?? domains[0] ?? null;

  const grantsQuery = useQuery({
    queryKey: permKeys.domainGrants(selected?.id ?? ''),
    queryFn: () => permApi.domainGrants(selected!.id),
    enabled: Boolean(selected),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: permKeys.all });
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  /*
   * OQ-244 — gửi lại lời mời.
   *
   * Gọi lại chính `POST /invitations` chứ không thêm đường API mới: câu
   * `INSERT … ON CONFLICT (email, domain_id) DO UPDATE` trong `inviteToDomain()`
   * đã gia hạn `expires_at` và phát token mới. "Gửi lại" và "mời lần đầu" là
   * cùng một thao tác nghiệp vụ, khác nhau ở chỗ hàng đã tồn tại hay chưa.
   *
   * Thêm endpoint riêng sẽ là bản sao thứ hai của cùng logic — đúng loại trùng
   * lặp vừa gây ra OQ-243.
   */
  const resendInvite = useMutation({
    mutationFn: (invite: PendingGrantView) =>
      permApi.invite(selected!.id, {
        email: invite.email,
        domainRoleId: invite.domainRole!.id,
        ...(invite.message ? { message: invite.message } : {}),
      }),
    onSuccess: async (_data, invite) => {
      await invalidate();
      toast(t('perm.inviteResent', { email: invite.email }));
    },
    onError,
  });

  const revokeInvite = useMutation({
    mutationFn: (pendingId: string) => permApi.revokeInvite(selected!.id, pendingId),
    onSuccess: async (_data, pendingId) => {
      const pending = grantsQuery.data?.pending.find((row) => row.id === pendingId);
      await invalidate();
      toast(t('perm.inviteRevoked', { email: pending?.email ?? '' }));
    },
    onError,
  });

  if (!domainsQuery.isLoading && domains.length === 0) {
    return (
      <div className="card-body">
        <EmptyState
          icon="domains"
          title={t('perm.emptyDomainTitle')}
          body={t('perm.emptyDomainBody')}
        />
      </div>
    );
  }

  return (
    <div className="card-body">
      <div className="split">
        {/* ── Cột trái: tên miền trong phạm vi (FR-075) ──────── */}
        <div className="card">
          <div className="card-head" style={{ padding: '10px 12px' }}>
            <span className="eyebrow grow">{t('perm.domains')}</span>
            <span className="small muted">
              {filtered.length}/{domains.length}
            </span>
          </div>
          <div className="pick-search">
            <input
              type="text"
              placeholder={t('perm.filterDomains')}
              aria-label={t('perm.filterDomains')}
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
          <div className="card-body" style={{ padding: 8 }}>
            <div className="picker scroll" data-od-id="perm-domain-picker">
              {domainsQuery.isLoading ? (
                <SkeletonCard rows={4} />
              ) : filtered.length === 0 ? (
                <div className="small muted" style={{ padding: 12 }}>
                  {t('perm.noDomainMatch')}
                </div>
              ) : (
                filtered.map((domain) => (
                  <button
                    key={domain.id}
                    type="button"
                    aria-pressed={domain.id === selected?.id}
                    onClick={() => setSelectedId(domain.id)}
                  >
                    <span className="zone-mark">
                      <Icon name="domains" size={15} />
                    </span>
                    <span className="who">
                      <b className="mono">{domain.name}</b>
                      <span>
                        {domain.addedByMe
                          ? t('perm.addedByYou')
                          : t('perm.ownedBy', { name: domain.owner?.fullName ?? '—' })}
                      </span>
                    </span>
                    <span className="n">{domain.memberCount}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Cột phải: người được gán tên miền (FR-076) ─────── */}
        <div className="card" data-od-id="perm-domain-detail">
          {selected ? (
            <>
              <div className="card-head">
                <span className="zone-mark">
                  <Icon name="domains" size={16} />
                </span>
                <div className="grow">
                  <h2 className="mono">{selected.name}</h2>
                  <div className="sub">
                    <span>{t('perm.recordsCount', { count: selected.recordCount })}</span> ·{' '}
                    {t('perm.ownerLevel').toLowerCase()} <b>{selected.owner?.fullName ?? '—'}</b>
                  </div>
                </div>
                <span className="chip">
                  <span className="dot" />
                  <b>{grantsQuery.data?.items.length ?? selected.memberCount}</b>&nbsp;
                  {t('perm.membersCount')}
                </span>
                <button
                  className="btn btn-sm btn-primary"
                  type="button"
                  onClick={() => setShowShare(true)}
                  data-od-id="perm-add-user"
                  hidden={!can('share.grant')}
                >
                  <Icon name="plus" size={15} />
                  {t('perm.addUser')}
                </button>
              </div>

              <AsyncSection
                loading={grantsQuery.isLoading}
                error={grantsQuery.error}
                data={grantsQuery.data}
                onRetry={() => void grantsQuery.refetch()}
                skeleton={<SkeletonCard rows={4} />}
              >
                {(data) => (
                  <DomainAccessTable
                    domainId={selected.id}
                    domainName={selected.name}
                    grants={data.items}
                    pending={data.pending}
                    odId="perm-domain-table"
                    onRevokeInvite={(pendingId) => revokeInvite.mutate(pendingId)}
                    onResendInvite={(invite) => resendInvite.mutate(invite)}
                  />
                )}
              </AsyncSection>
            </>
          ) : (
            <div className="card-body">
              <EmptyState title={t('perm.emptyDomainTitle')} />
            </div>
          )}
        </div>
      </div>

      {selected && showShare ? (
        <ShareGrantDialog
          open
          domainId={selected.id}
          domainName={selected.name}
          onClose={() => setShowShare(false)}
        />
      ) : null}
    </div>
  );
}
